import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FoodFeedbackModalContext } from './FoodFeedbackModal';
import { useShowPerceivedValueStage } from './PerceivedValueStage/PerceivedValueStage';
import { useDetectChanges } from './useDetectChanges';
import {
  type RatablePairingItem,
  type RatableProductItem,
  useRatableItems,
} from './useRatableItems';
import { useRatedItemCount } from './useRatedItemCount';

export enum Stage {
  Loading,
  RatablePairings,
  RatableProducts,
  ThankYou,
  NeedHelpWithYourOrder,
  PerceivedValue,
}

type Conditions =
  | 'showRatablePairings'
  | 'showRatableProducts'
  | 'showPerceivedValue'
  | 'hasIssues'
  | 'hasChanges';

const previousStageMap: Partial<
  Record<Stage, (conditions: Record<Conditions, boolean>) => Stage | undefined>
> = {
  [Stage.RatablePairings]: (conditions) => {
    if (conditions.showRatableProducts) return Stage.RatableProducts;
  },
  [Stage.PerceivedValue]: (conditions) => {
    if (conditions.showRatablePairings) return Stage.RatablePairings;
    if (conditions.showRatableProducts) return Stage.RatableProducts;
  },
  [Stage.NeedHelpWithYourOrder]: (conditions) => {
    if (conditions.showPerceivedValue) return Stage.PerceivedValue;
    if (conditions.showRatablePairings) return Stage.RatablePairings;
    if (conditions.showRatableProducts) return Stage.RatableProducts;
  },
} as const;

const nextStageMap: Partial<
  Record<
    Stage,
    (
      conditions: Record<Conditions, boolean>
    ) => Stage | 'closeModal' | undefined
  >
> = {
  [Stage.RatablePairings]: (conditions) => {
    if (conditions.hasIssues) return Stage.NeedHelpWithYourOrder;
    if (conditions.showPerceivedValue) return Stage.PerceivedValue;
    if (conditions.hasChanges) return Stage.ThankYou;
    return 'closeModal';
  },
  [Stage.RatableProducts]: (conditions) => {
    if (conditions.showRatablePairings) return Stage.RatablePairings;
    if (conditions.hasIssues) return Stage.NeedHelpWithYourOrder;
    if (conditions.showPerceivedValue) return Stage.PerceivedValue;
    if (conditions.hasChanges) return Stage.ThankYou;
    return 'closeModal';
  },
  [Stage.PerceivedValue]: (conditions) => {
    if (conditions.hasChanges) return Stage.ThankYou;
    return 'closeModal';
  },
  [Stage.NeedHelpWithYourOrder]: () => Stage.ThankYou,
};

type UseFoodFeedbackModalReturn = {
  prevStage?: () => void;
  nextStage?: () => void;
  stage: Stage;
  pairings: ReturnType<typeof useRatableItems>['pairings'];
  products: ReturnType<typeof useRatableItems>['products'];
  isPending: boolean;
  changeDetection: ReturnType<typeof useDetectChanges>;
  onRating: ({
    item,
    rating,
  }: {
    item: RatablePairingItem | RatableProductItem;
    rating: boolean | null;
  }) => void;
  itemCounts: {
    pairingsQuantityPresented: number;
    productsQuantityPresented: number;
  } & ReturnType<typeof useRatedItemCount>['ratedItemCount'];
};

export type UseFoodFeedbackModalProps = {
  orderId?: number;
  recentOrderCount?: number;
  dismissModal?: (args: {
    counts: {
      pairingsQuantityPresented: number;
      productsQuantityPresented: number;
    } & ReturnType<typeof useRatedItemCount>['ratedItemCount'];
    dismissalType: 'submit' | 'close' | null;
  }) => void;
};

export function useFoodFeedbackModal(
  props: UseFoodFeedbackModalProps
): UseFoodFeedbackModalReturn {
  const { orderId, recentOrderCount, dismissModal: passedDismissModal } = props;

  const changeDetection = useDetectChanges();

  const {
    isPending,
    pairings: ratableItemsPairings,
    products: ratableItemsProducts,
  } = useRatableItems({ recentOrderCount });

  let pairings = ratableItemsPairings;
  let products = ratableItemsProducts;

  if (orderId) {
    pairings = ratableItemsPairings.filter(
      (pairing) => pairing.orderNumber === orderId
    );
    products = ratableItemsProducts.filter(
      (product) => product.orderNumber === orderId
    );
  }

  const { onRating: ratedItemCountOnRating, ratedItemCount } =
    useRatedItemCount();

  const { issues } = useContext(FoodFeedbackModalContext);

  const showRatablePairings = pairings.length > 0;
  const showRatableProducts = products.length > 0;

  const { shouldRender: showPerceivedValue } = useShowPerceivedValueStage();

  const itemCounts = useMemo(
    () => ({
      pairingsQuantityPresented: pairings.length,
      productsQuantityPresented: products.length,
      ...ratedItemCount,
    }),
    [pairings.length, products.length, ratedItemCount]
  );

  const getInitialStage = useCallback(() => {
    if (isPending) {
      return Stage.Loading;
    } else if (showRatableProducts) {
      return Stage.RatableProducts;
    } else if (showRatablePairings) {
      return Stage.RatablePairings;
    } else {
      return Stage.ThankYou;
    }
  }, [isPending, showRatablePairings, showRatableProducts]);

  const [stage, setStage] = useState<Stage>(getInitialStage());

  const stageConditionals = {
    showRatablePairings,
    showRatableProducts,
    showPerceivedValue: Boolean(showPerceivedValue),
    hasIssues: Boolean(issues && issues.size > 0),
    hasChanges: Boolean(changeDetection.hasChanges()),
  };

  const previousStageValue = previousStageMap?.[stage]?.(stageConditionals);

  const prevStage = previousStageValue
    ? () => setStage(previousStageValue)
    : undefined;

  const nextStageValue = nextStageMap[stage]?.(stageConditionals);

  const nextStage = nextStageValue
    ? () => {
        const currentConditions = {
          ...stageConditionals,
          hasChanges: Boolean(changeDetection.hasChanges()),
        };

        const transitionValue = nextStageMap[stage]?.(currentConditions);

        if (transitionValue === 'closeModal') {
          return passedDismissModal?.({
            counts: itemCounts,
            dismissalType: 'close',
          });
        }

        if (transitionValue) {
          setStage(transitionValue);
        }
      }
    : undefined;

  // Our data changed at some point. Make sure we're not showing a stale stage.
  useEffect(() => {
    setStage(getInitialStage());
  }, [getInitialStage]);

  useEffect(() => {
    const fireEvent = () => {
      if (document.visibilityState === 'hidden') {
        passedDismissModal?.({
          counts: itemCounts,
          dismissalType: null,
        });
      }
    };

    document.addEventListener('visibilitychange', fireEvent);

    return () => {
      document.removeEventListener('visibilitychange', fireEvent);
    };
  }, [itemCounts, passedDismissModal]);

  return {
    prevStage,
    nextStage,
    stage,
    pairings,
    products,
    isPending,
    changeDetection,
    onRating: ratedItemCountOnRating,
    itemCounts,
  };
}
